/**
 * Transforms the raw export CSV returned by the medical PA orders Postgres
 * search endpoint before it is downloaded by the user.
 *
 * Applies the following changes:
 *  - Merges `first_name` and `last_name` into a single `patient_name` column
 *    and drops the originals.
 *  - Reformats `created_at_iso`, `date_of_service_iso`, `date_of_work_iso`,
 *    and `date_of_birth` values into `MM/DD/YYYY`.
 *  - Coerces every cell to a string and trims surrounding whitespace.
 *  - Optionally prepends a metadata preamble with the total record count and
 *    a human-readable summary of the applied filters.
 *
 * @module transformExportCsv
 */
import moment from "moment";
import Papa from "papaparse";

/**
 * Optional filter context used to render a human-readable preamble in the CSV.
 * Mirrors the shape of `SearchForMedicalPaOrdersFromPostgresParams` but only
 * the user-facing filter fields are read.
 */
export interface ExportFilterSummary {
  query?: string;
  facet_filters?: Record<string, string[]>;
  created_at_start?: string;
  created_at_end?: string;
  date_of_service_start?: string;
  date_of_service_end?: string;
  date_of_work_start?: string;
  date_of_work_end?: string;
  date_of_work_only?: boolean;
}

export interface TransformExportCsvOptions {
  filters?: ExportFilterSummary;
}

const DATE_COLUMNS = [
  "created_at_iso",
  "date_of_service_iso",
  "date_of_work_iso",
  "date_of_birth",
] as const;

type DateColumn = (typeof DATE_COLUMNS)[number];

type CsvRow = Record<string, unknown>;
type NormalizedRow = Record<string, string>;

/**
 * Parses a raw CSV string into structured rows and the original header order.
 */
const parseCsv = (
  csv: string,
): { rows: CsvRow[]; originalFields: string[] } => {
  const { data, meta } = Papa.parse<CsvRow>(csv, {
    header: true,
    skipEmptyLines: true,
  });
  return { rows: data, originalFields: meta.fields ?? [] };
};

/**
 * Drops `first_name`/`last_name` from the header list and prepends
 * `patient_name` if either source column was present.
 */
const buildOutputFields = (originalFields: string[]): string[] => {
  const filtered = originalFields.filter(
    (field) => field !== "first_name" && field !== "last_name",
  );
  const hasNameColumn =
    originalFields.includes("first_name") ||
    originalFields.includes("last_name");
  return hasNameColumn ? ["patient_name", ...filtered] : filtered;
};

/**
 * Coerces a value to a trimmed string, treating null/undefined as empty.
 */
const toTrimmedString = (value: unknown): string => String(value ?? "").trim();

const isDateColumn = (field: string): field is DateColumn =>
  (DATE_COLUMNS as readonly string[]).includes(field);

/**
 * Formats an ISO/parseable date string as `MM/DD/YYYY` in UTC.
 *
 * Parsing in UTC keeps the rendered calendar day independent of the viewer's
 * local timezone, matching how dates are displayed on the orders screen
 * (`formatDateToUTCWithFormat`). Without this, an ISO timestamp such as
 * `2026-07-01T00:00:00Z` would render as `06/30/2026` for viewers behind UTC,
 * shifting the whole exported report back by a day.
 *
 * Returns the original value when it cannot be parsed, and an empty string when
 * blank.
 */
const formatDate = (value: string): string => {
  if (!value) return "";
  const parsed = moment.utc(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
};

/**
 * Combines `first_name` and `last_name` from a row into a single
 * space-separated patient name with both parts trimmed.
 */
const buildPatientName = (row: CsvRow): string => {
  const first = toTrimmedString(row.first_name);
  const last = toTrimmedString(row.last_name);
  return [first, last].filter(Boolean).join(" ");
};

/**
 * Normalizes a single CSV row: trims values, formats date columns, and
 * synthesizes `patient_name` when present in the output fields.
 */
const transformRow = (row: CsvRow, fields: string[]): NormalizedRow => {
  const next: NormalizedRow = {};

  for (const field of fields) {
    if (field === "patient_name") {
      next.patient_name = buildPatientName(row);
      continue;
    }

    const value = toTrimmedString(row[field]);
    next[field] = isDateColumn(field) ? formatDate(value) : value;
  }

  return next;
};

/**
 * Converts a snake_case or kebab-case field key into a Title Case label
 * suitable for display (e.g. `auth_status` → `Auth Status`).
 */
const humanizeKey = (key: string): string =>
  key
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

/**
 * Formats a date range as `MM/DD/YYYY - MM/DD/YYYY`, handling open-ended
 * ranges where only one bound is provided.
 */
const formatDateRange = (start?: string, end?: string): string | null => {
  const formattedStart = start ? formatDate(start) : "";
  const formattedEnd = end ? formatDate(end) : "";
  if (!formattedStart && !formattedEnd) return null;
  if (formattedStart && formattedEnd)
    return `${formattedStart} - ${formattedEnd}`;
  if (formattedStart) return `From ${formattedStart}`;
  return `Until ${formattedEnd}`;
};

/**
 * Builds an array of `[label, value]` pairs describing the active filters in a
 * human-readable form. Empty/missing filters are skipped.
 */
const buildFilterSummaryEntries = (
  filters: ExportFilterSummary,
): Array<[string, string]> => {
  const entries: Array<[string, string]> = [];

  if (filters.query?.trim()) {
    entries.push(["Search Query", filters.query.trim()]);
  }

  const createdRange = formatDateRange(
    filters.created_at_start,
    filters.created_at_end,
  );
  if (createdRange) entries.push(["Created At", createdRange]);

  const dosRange = formatDateRange(
    filters.date_of_service_start,
    filters.date_of_service_end,
  );
  if (dosRange) entries.push(["Date of Service", dosRange]);

  const dowRange = formatDateRange(
    filters.date_of_work_start,
    filters.date_of_work_end,
  );
  if (dowRange) entries.push(["Date of Work", dowRange]);

  if (filters.date_of_work_only) {
    entries.push(["Date of Work Only", "Yes"]);
  }

  for (const [key, values] of Object.entries(filters.facet_filters ?? {})) {
    if (!values || values.length === 0) continue;
    entries.push([humanizeKey(key), values.join(", ")]);
  }

  return entries;
};

const sectionBanner = (title: string): string[] => [`=== ${title} ===`];

/**
 * Builds the preamble text (metadata + filters) that gets prepended above the
 * tabular CSV section, organized into clearly labeled sections separated by
 * blank rows.
 */
const buildPreamble = (
  totalRecords: number,
  filters: ExportFilterSummary | undefined,
): string => {
  const lines: string[][] = [];

  lines.push(sectionBanner("EXPORT METADATA"));
  lines.push(["Exported At", moment().format("MM/DD/YYYY hh:mm A")]);
  lines.push(["Total Records", String(totalRecords)]);
  lines.push([]);

  const filterEntries = filters ? buildFilterSummaryEntries(filters) : [];
  if (filterEntries.length > 0) {
    lines.push(sectionBanner("FILTERS APPLIED"));
    lines.push(...filterEntries);
    lines.push([]);
  }

  lines.push(sectionBanner("DATA"));
  return Papa.unparse(lines);
};

/**
 * Parses the given CSV string, applies export-specific transforms, and returns
 * the re-serialized CSV string ready for download. When `options.filters` is
 * provided, a human-readable preamble (export timestamp, total record count,
 * and applied filters) is prepended above the table.
 *
 * @param csv - Raw CSV text from the backend export endpoint
 * @param options - Optional transform options, e.g. filter context
 * @returns Transformed CSV string
 */
export const transformExportCsv = (
  csv: string,
  options: TransformExportCsvOptions = {},
): string => {
  const { rows, originalFields } = parseCsv(csv);
  const fields = buildOutputFields(originalFields);
  const transformed = rows.map((row) => transformRow(row, fields));
  const tableCsv = Papa.unparse(transformed, { columns: fields });

  const preamble = buildPreamble(transformed.length, options.filters);
  return `${preamble}\n${tableCsv}`;
};
